import React from "react";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MoneyInput } from "../../../components/form-components/MoneyInput";
import { secondaryButtonCls } from "../../../components/ui-components/buttonStyles.js";
import { AUTHORITY_OBJECT_TYPE_OPTIONS, REVIEW_FREQUENCY_OPTIONS } from "./constants";
import { WizardCard, WizardSection, selectCls } from "./WizardUI";

// Step 4 (spec §3.4 / §4): owner (required) + review cadence + optional
// default approval-authority rows (reuses C's approval_authority — a
// role-level DEFAULT, materialized onto the user's own grant on
// assignment, see backend_go internal/users.CreateOrgRoleParams) +
// includes-sensitive (auto-detected from the step-3 capability set but
// confirmable here) + is_scoped ("used with an assigned data scope").
export default function StepGovernance({
  userOptions,
  owner,
  setOwner,
  reviewFrequency,
  setReviewFrequency,
  includesSensitive,
  setIncludesSensitive,
  isScoped,
  setIsScoped,
  defaultAuthority,
  setDefaultAuthority,
}) {
  const addAuthorityRow = () => {
    setDefaultAuthority((rows) => [...rows, { object_type: AUTHORITY_OBJECT_TYPE_OPTIONS[0].value, max_amount: "" }]);
  };
  const updateAuthorityRow = (idx, patch) => {
    setDefaultAuthority((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeAuthorityRow = (idx) => {
    setDefaultAuthority((rows) => rows.filter((_, i) => i !== idx));
  };

  return (
    <WizardCard title="Configure governance" subtitle="Every organisation role needs an accountable owner and a review cadence.">
      <div className="space-y-4">
        <WizardSection title="Owner" helperText="Accountable for this role's access and its periodic review. Required.">
          <select
            aria-label="Owner"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            className={selectCls}
          >
            <option value="">Select an owner…</option>
            {userOptions.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
        </WizardSection>

        <WizardSection title="Review frequency" helperText="How often this role's access should be re-reviewed.">
          <select
            aria-label="Review frequency"
            value={reviewFrequency}
            onChange={(e) => setReviewFrequency(e.target.value)}
            className={selectCls}
          >
            {REVIEW_FREQUENCY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </WizardSection>

        <WizardSection
          title="Approval authority"
          helperText="Optional. Seeds a default approval limit that's granted to whoever this role is assigned to."
        >
          <div className="space-y-2">
            {defaultAuthority.map((row, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <select
                  aria-label={`Approval object type ${idx + 1}`}
                  value={row.object_type}
                  onChange={(e) => updateAuthorityRow(idx, { object_type: e.target.value })}
                  className={`${selectCls} flex-1`}
                >
                  {AUTHORITY_OBJECT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <div className="w-[160px] shrink-0">
                  <label htmlFor={`authority-amount-${idx}`} className="sr-only">
                    Max approval amount {idx + 1}
                  </label>
                  <MoneyInput
                    id={`authority-amount-${idx}`}
                    value={row.max_amount}
                    onChange={(n) => updateAuthorityRow(idx, { max_amount: n })}
                    allowNegative={false}
                    min={0}
                    decimals={2}
                    placeholder="Max amount"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeAuthorityRow(idx)}
                  aria-label={`Remove approval authority row ${idx + 1}`}
                  className="text-text-3 hover:text-danger inline-flex items-center justify-center shrink-0 border-0 box-border"
                  style={{ height: 36, width: 36 }}
                >
                  <FontAwesomeIcon icon={icon({ name: "trash", style: "solid" })} />
                </button>
              </div>
            ))}
            <button type="button" className={secondaryButtonCls} onClick={addAuthorityRow}>
              Add approval limit
            </button>
          </div>
        </WizardSection>

        <label className="flex items-start gap-2.5 rounded-lg border border-border bg-surface-2/50 p-4 cursor-pointer">
          <input
            type="checkbox"
            checked={includesSensitive}
            onChange={(e) => setIncludesSensitive(e.target.checked)}
            className="mt-0.5 rounded border-border accent-primary shrink-0"
          />
          <span className="min-w-0">
            <span className="block text-sm text-text">Includes sensitive financial data</span>
            <span className="block text-xs text-text-2 mt-0.5">
              Auto-detected from the selected capabilities — confirm or override.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2.5 rounded-lg border border-border bg-surface-2/50 p-4 cursor-pointer">
          <input
            type="checkbox"
            checked={isScoped}
            onChange={(e) => setIsScoped(e.target.checked)}
            className="mt-0.5 rounded border-border accent-primary shrink-0"
          />
          <span className="min-w-0">
            <span className="block text-sm text-text">This role is used with an assigned data scope</span>
            <span className="block text-xs text-text-2 mt-0.5">
              Tells the control-check this role is meant to be constrained to a scope, not tenant-wide.
            </span>
          </span>
        </label>
      </div>
    </WizardCard>
  );
}
